import {
  REQUEST_PAYPAL_ELIGIBILITY,
  RECEIVE_PAYPAL_ELIGIBILITY,
  PAYPAL_ELIGIBILITY_FAILURE,
} from 'constants/ActionTypes';
import { purchase, paypal as paypalApi } from 'js-api-client';
import { createPayment } from '@/services/purchase/createPayment';
import { getDistinctId } from 'user-analytics';
import { getSessionId } from 'utils/session';
import { trackPaymentRedirect } from 'metrics/user-analytics/errorTracker';
import { requestPayment, receivePayment, updatePurchase } from './purchase';
import { paymentFailureHandler, paymentPollingHandler } from './payment';
import paypalFactory from '../../payments/core/factories/paypal';
import paypalEngine from '../../payments/core/engines/paypal';
import { getPayPalSDKConfig } from '../utils/paypalV6Config';

/**
 * Gets the client token from the API.
 * @returns {Promise<string>} The client token.
 */
async function getClientTokenFromApi() {
  const { client_token: clientToken } = await paypalApi.getClientToken();
  if (typeof clientToken === 'string' && clientToken.length > 0) {
    return clientToken;
  }
  throw new Error('[PayPal] Error getting client token');
}

/**
 * Gets the order id from the API.
 * @returns {Promise<string>} The order id.
 */
async function getOrderIdFromApi(purchaseToken) {
  const { order_id: orderId } = await paypalApi.getOrderId(purchaseToken);
  if (typeof orderId === 'string' && orderId.length > 0) {
    return orderId;
  }
  throw new Error('[PayPal] Error getting order id');
}

/**
 * Confirms the PayPal payment.
 *
 * @param {string} purchaseToken - The token associated with the purchase.
 * @param {string} paymentId - The ID of the PayPal payment to confirm.
 * @returns {Function} A thunk action that dispatches actions based on the PayPal payment confirmation process.
 */
export function confirmPaypalPayment(purchaseToken, paymentId) {
  return (dispatch) => {
    dispatch(requestPayment());

    return purchase
      .confirmPaypalPayment(purchaseToken, paymentId, {
        onReceivePayment: (response) => {
          if (response.payload.status === 'failed' || response.payload.status === 'rejected') {
            // Manually navigate to the failed payment page
            window.location = `/payment/${purchaseToken}/${paymentId}/failure`;
          } else {
            paymentPollingHandler(dispatch, purchaseToken, response);
          }
        },
      })
      .catch((error) => paymentFailureHandler(dispatch, error));
  };
}

/**
 * Checks PayPal payment methods eligibility.
 * Verifies which PayPal payment options (Standard, Pay Later, Credit) are available.
 * @returns {Function} Redux thunk action
 */
export function checkPaypalEligibility() {
  return async (dispatch) => {
    dispatch({ type: REQUEST_PAYPAL_ELIGIBILITY });

    try {
      // Get PayPal configuration
      const paypalSDKConfig = getPayPalSDKConfig();

      const paypalConfig = {
        sandbox: paypalSDKConfig.sandbox,
        locale: paypalSDKConfig.locale,
        currency: paypalSDKConfig.currency,
        sdkUrl: paypalSDKConfig.sdkUrl,
      };

      // Create PayPal instance to check eligibility
      const paypalPayment = paypalFactory.create({
        engine: 'paypal',
        paypalConfig,
      });

      // Only fetch clientToken if SDK is not already initialized
      // This avoids unnecessary backend calls when the SDK singleton is already set up
      let clientToken = null;
      if (!paypalEngine.isInitialized()) {
        clientToken = await getClientTokenFromApi();
      }

      // Check eligibility for all methods (clientToken only used if SDK needs initialization)
      const eligibility = await paypalPayment.checkEligibility(paypalConfig, clientToken);

      dispatch({
        type: RECEIVE_PAYPAL_ELIGIBILITY,
        payload: eligibility,
      });
    } catch (error) {
      console.error('[PayPal] Eligibility check failed:', error);
      dispatch({
        type: PAYPAL_ELIGIBILITY_FAILURE,
        error: error.message,
      });
    }
  };
}

/**
 * Initiates a PayPal payment attempt.
 * Handles the complete payment flow for different PayPal payment types.
 * @param {string} paymentType - Type of PayPal payment ('standard', 'paylater', 'credit')
 * @param {Object} options - Additional options (e.g., productCode for Pay Later)
 * @returns {Function} Redux thunk action
 */
export function paypalPaymentAttempt(paymentType = 'standard', options = {}) {
  return async (dispatch, getState) => {
    dispatch(updatePurchase(true));

    let paymentId = null;
    try {
      const state = getState();
      const purchaseState = state.purchase;

      // Step 1: Get PayPal configuration
      const paypalSDKConfig = getPayPalSDKConfig();

      const paypalConfig = {
        sandbox: paypalSDKConfig.sandbox,
        presentationMode: paypalSDKConfig.presentationMode,
        locale: paypalSDKConfig.locale,
        currency: paypalSDKConfig.currency,
        sdkUrl: paypalSDKConfig.sdkUrl,
      };

      // Step 2: Create PayPal instance
      const paypalPayment = paypalFactory.create({
        engine: 'paypal',
        paypalConfig,
      });

      // Step 3: Create payload
      const purchaseToken = purchaseState.get('token');
      const selectedPaymentMethod = purchaseState.get('selectedPaymentMethod');

      const paypalInfo = {
        payment_type: 'paypal',
        payment_engine: selectedPaymentMethod?.engine ?? 'paypal',
        payment_provider: selectedPaymentMethod?.provider ?? 'paypal',
        tracker_id: getDistinctId(),
        device_session_id: getSessionId(),
        monthly_selected_plan: 1,
      };

      const paypalPayload = await paypalPayment.createPayload(paypalInfo);

      // Step 4: Create pending payment
      const paymentResponse = await createPayment(purchaseToken, paypalPayload, {
        init: false,
      });

      paymentId = paymentResponse?.payload?.id;
      if (!paymentId) {
        throw new Error('Invalid payment response from server');
      }

      // Step 5: Get Order ID
      const orderId = await getOrderIdFromApi(purchaseToken);

      const paymentData = {
        orderIdPromise: Promise.resolve({ orderId }),
        paymentType, // 'standard', 'paylater', 'credit'
        ...options, // productCode for paylater
      };

      // Flag to prevent onCancel from firing after onApprove.
      // PayPal v6 SDK fires onCancel when the modal closes, even after a successful approval
      // (race condition between popup close and SDK approval processing).
      let approved = false;

      const callbacks = {
        onApprove: () => {
          approved = true;
          dispatch(confirmPaypalPayment(purchaseToken, paymentId));
        },
        onCancel: () => {
          if (approved) return;
          paypalApi
            .cancelPayment(paymentId)
            .then((resp) => {
              dispatch(receivePayment(resp));
            })
            .catch((error) => {
              console.error('[PayPal] Cancel payment failed:', error);
              paymentFailureHandler(dispatch, error);
            });
        },
        onError: (error) => {
          console.error('[PayPal] Payment error:', error);
          paypalApi
            .failedPayment(paymentId, { error })
            .then(() => {
              paymentFailureHandler(dispatch, error);
            })
            .catch((apiError) => {
              console.error('[PayPal] Failed payment API call failed:', apiError);
              paymentFailureHandler(dispatch, error);
            });
        },
      };

      // Step 6: Only fetch clientToken if SDK is not already initialized
      // This avoids unnecessary backend calls when the SDK singleton is already set up
      let clientToken = null;
      if (!paypalEngine.isInitialized()) {
        clientToken = await getClientTokenFromApi();
      }

      // Step 7: Process payment with specific type (clientToken only used if SDK needs initialization)
      trackPaymentRedirect({
        provider: 'paypal',
        isExpected: true,
        presentationMode: paypalConfig.presentationMode,
      });
      await paypalPayment.processPayment(paypalConfig, paymentData, callbacks, clientToken);
      dispatch(updatePurchase(false));
    } catch (error) {
      console.error(`[PayPal ${paymentType}] Payment attempt failed:`, error);
      if (paymentId) {
        try {
          await paypalApi.failedPayment(paymentId, { error });
        } catch (failedPaymentError) {
          console.error('[PayPal] Failed payment API call failed:', failedPaymentError);
        }
      }
      paymentFailureHandler(dispatch, error);
    }
  };
}
